import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import HaloRing from "@/components/HaloRing";
import HaloWordmark from "@/components/HaloWordmark";
import { Button } from "@/components/ui/button";
import {
  Sun, CalendarClock, KanbanSquare, Receipt, Mail, Sparkles, Check,
  ArrowRight, Menu, X, Users, FileText, Clock, Repeat, Palette, ShieldCheck,
  CalendarDays, CheckSquare, FolderKanban, DollarSign, Loader2, Quote,
} from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const SHOTS = [
  { id: "today", label: "Home dashboard", img: "/marketing/today-dashboard.jpg", caption: "Morning brief, money watch and a live focus timer — the widgets are yours to arrange." },
  { id: "projects", label: "Projects & timeline", img: "/marketing/projects.jpg", caption: "Every project on a Gantt timeline with phases, tasks and a folder tree per stage." },
  { id: "tasks", label: "Tasks", img: "/marketing/tasks.jpg", caption: "Tasks with subtasks, assignees and recurrence — auto-packed into your working windows." },
  { id: "finance", label: "Invoices", img: "/marketing/finance.jpg", caption: "Branded invoices, payments and expenses, with overdue chasing handled for you." },
];

const FEATURES = [
  {
    id: "today", icon: Sun, tag: "Command centre", span: "lg:col-span-3",
    title: "A day that plans itself",
    body: "Open Mahano and the morning brief is already written: what's due, what's overdue, what money is outstanding. Drag the widgets into the shape of your own day and start the focus timer.",
  },
  {
    id: "scheduling", icon: CalendarClock, tag: "Intelligent calendar", span: "lg:col-span-3",
    title: "Tasks packed into your real hours",
    body: "Define working hours, after hours and weekend windows. Mahano slots every task into the gaps that actually exist, and repacks the day the moment something slips.",
  },
  {
    id: "projects", icon: KanbanSquare, tag: "Projects", span: "lg:col-span-2",
    title: "Kanban and Gantt, same project",
    body: "Phase templates, milestones, assignees, subtask gating and recurring work — switch between board and timeline without losing the thread.",
  },
  {
    id: "crm", icon: Users, tag: "CRM", span: "lg:col-span-2",
    title: "Clients, not spreadsheets",
    body: "People, companies, documents and follow-ups in one place, wired straight into projects and invoices.",
  },
  {
    id: "finance", icon: Receipt, tag: "Finance", span: "lg:col-span-2",
    title: "Invoices that chase themselves",
    body: "Branded PDF invoices, expenses and payments — plus a daily job that politely nudges overdue clients on your schedule.",
  },
  {
    id: "email", icon: Mail, tag: "Email your way", span: "lg:col-span-3",
    title: "Your provider, your deliverability",
    body: "Postmark, Resend, SendGrid, Mailgun, Amazon SES, Gmail or your own SMTP server. Pick one in Settings, send a test, done.",
  },
  {
    id: "ai", icon: Sparkles, tag: "AI discovery", span: "lg:col-span-3",
    title: "Industry news, tuned to you",
    body: "Set the topics you care about and Mahano searches the live web each morning for the releases, funding calls and moves worth knowing.",
  },
];

const WHITE_LABEL = [
  { icon: Palette, text: "Your name, logo, colours and font pairing across the whole app" },
  { icon: FileText, text: "Rename any module — “Projects” becomes “Deliverables” if that's your language" },
  { icon: Mail, text: "Send from your own domain through your own email provider" },
  { icon: CalendarClock, text: "Sync to your own Google Calendar, not someone else's cloud" },
  { icon: ShieldCheck, text: "Turn modules off entirely so clients only see what you want them to" },
];

const PRICE_INCLUDES = [
  "All 12 modules — CRM, projects, tasks, finance, calendar, campaigns, news, analytics",
  "Motion-style scheduling with custom working windows",
  "Kanban boards, Gantt timelines and project templates",
  "Branded PDF invoices, expenses and automated overdue chasing",
  "Bring your own email provider (Postmark, Resend, SendGrid, Mailgun, SES, Gmail, SMTP)",
  "Two-way Google Calendar sync",
  "AI news discovery on your topics",
  "Full white-label branding and module renaming",
  "CSV import and export of everything you put in",
];

const FAQ = [
  {
    q: "What does “white-label” actually mean here?",
    a: "Your studio's name, logo, colours, fonts and module labels replace ours everywhere in the app — sidebar, invoices, emails. You can also switch modules off so the workspace only shows what you want it to.",
  },
  {
    q: "How does the intelligent scheduling work?",
    a: "You set the windows you're actually willing to work in — say weekdays 9–6, after hours 6–9 and Saturday mornings. Give a task an estimate and Mahano drops it into the next real gap, then repacks the rest of the day when something runs over.",
  },
  {
    q: "Do invoice reminders send themselves?",
    a: "Yes. Choose the days past due you want to chase on, write the message once, and a daily job emails the client through your own email provider. Any single invoice can be paused from its edit dialog.",
  },
  {
    q: "Which email providers can I use?",
    a: "Postmark, Resend, SendGrid, Mailgun, Amazon SES, Gmail (via your Google account or an app password) or any SMTP server. Keys are stored server-side and there's a test-send button so you're never guessing.",
  },
  {
    q: "Can I get my data out?",
    a: "Always. Every module exports to CSV from the Import / Export page, and the same importer brings your existing clients and projects in.",
  },
];

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`px-6 md:px-10 lg:px-16 ${className}`}>
    <div className="mx-auto w-full max-w-6xl">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--studio-primary))]">
    <span className="h-1 w-1 rounded-full bg-[hsl(var(--halo-teal))]" />
    {children}
  </span>
);

const DemoRequestDialog = ({ open, onOpenChange }) => {
  const [f, setF] = useState({ name: "", email: "", company: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (patch) => setF((s) => ({ ...s, ...patch }));

  const submit = async () => {
    if (!f.name.trim() || !f.email.trim()) { toast.error("Your name and email, please"); return; }
    setBusy(true);
    try {
      await api.post("/public/demo-request", f);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send that — try again shortly");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setSent(false); }}>
      <DialogContent className="max-w-md" data-testid="demo-request-dialog">
        <DialogHeader>
          <DialogTitle>{sent ? "Thanks — we'll be in touch" : "Book a white-label demo"}</DialogTitle>
          <DialogDescription>
            {sent
              ? "Your request has landed. Someone will reach out with a walkthrough and pricing for running Mahano OS under your own brand."
              : "Reselling Mahano OS under your own name and colours? Tell us a little and we'll set up a walkthrough."}
          </DialogDescription>
        </DialogHeader>
        {!sent && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Your name</Label>
              <Input data-testid="demo-name" value={f.name} onChange={(e) => set({ name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input data-testid="demo-email" type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Company</Label>
              <Input data-testid="demo-company" value={f.company} onChange={(e) => set({ company: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">What are you hoping to run on it?</Label>
              <Textarea data-testid="demo-notes" rows={3} value={f.notes} onChange={(e) => set({ notes: e.target.value })} />
            </div>
          </div>
        )}
        <DialogFooter>
          {sent ? (
            <Button onClick={() => onOpenChange(false)} data-testid="demo-done">Close</Button>
          ) : (
            <Button onClick={submit} disabled={busy} data-testid="demo-submit">
              {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : "Request a demo"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Landing() {
  const { user, loading } = useAuth();
  const [shot, setShot] = useState(SHOTS[0].id);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const active = SHOTS.find((s) => s.id === shot) || SHOTS[0];

  return (
    <div className="min-h-screen bg-background text-foreground landing-root" data-testid="landing-page">
      {/* ---------- Nav ---------- */}
      <header
        data-testid="landing-nav"
        className={`sticky top-0 z-50 border-b backdrop-blur-xl ${scrolled ? "border-border/70 bg-background/85" : "border-transparent bg-background/40"}`}
        style={{ transitionProperty: "background-color, border-color", transitionDuration: "220ms" }}
      >
        <div className="mx-auto flex w-full max-w-6xl items-center gap-6 px-6 py-4 md:px-10 lg:px-16">
          <Link to="/welcome" className="flex items-center gap-2.5" data-testid="landing-logo">
            <HaloRing size={28} />
            <HaloWordmark height={18} />
          </Link>
          <nav className="ml-6 hidden items-center gap-7 text-sm text-muted-foreground lg:flex">
            {[["Features", "features"], ["Scheduling", "scheduling"], ["White-label", "whitelabel"], ["Pricing", "pricing"], ["FAQ", "faq"]].map(([label, id]) => (
              <a key={id} href={`#${id}`} data-testid={`nav-link-${id}`}
                 className="hover:text-foreground" style={{ transitionProperty: "color", transitionDuration: "160ms" }}>
                {label}
              </a>
            ))}
          </nav>
          <div className="ml-auto hidden items-center gap-2 md:flex">
            <Link to="/login" data-testid="nav-signin">
              <Button variant="ghost" size="sm">Sign in</Button>
            </Link>
            <Link to="/login?mode=signup" data-testid="nav-signup">
              <Button size="sm" className="rounded-full px-4">Start free</Button>
            </Link>
          </div>
          <button className="ml-auto md:hidden" data-testid="nav-mobile-toggle"
                  onClick={() => setMenuOpen((v) => !v)} aria-label="Menu">
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {menuOpen && (
          <div className="border-t border-border bg-background/95 px-6 py-4 md:hidden" data-testid="nav-mobile-menu">
            <div className="flex flex-col gap-4 text-sm">
              {[["Features", "features"], ["Scheduling", "scheduling"], ["White-label", "whitelabel"], ["Pricing", "pricing"], ["FAQ", "faq"]].map(([label, id]) => (
                <a key={id} href={`#${id}`} onClick={() => setMenuOpen(false)}
                   data-testid={`nav-mobile-link-${id}`} className="text-muted-foreground">{label}</a>
              ))}
              <div className="flex gap-2 pt-2">
                <Link to="/login" className="flex-1" data-testid="nav-mobile-signin">
                  <Button variant="outline" className="w-full">Sign in</Button>
                </Link>
                <Link to="/login?mode=signup" className="flex-1" data-testid="nav-mobile-signup">
                  <Button className="w-full">Start free</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ---------- Hero ---------- */}
      <div className="relative overflow-hidden">
        <div className="landing-aurora" aria-hidden="true" />
        <Section className="relative pt-16 pb-10 md:pt-24">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div className="landing-rise">
              <Eyebrow>Life and work, in harmony</Eyebrow>
              <h1 className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl" style={{ lineHeight: 1.04 }}>
                Everything in sync.<br />
                <span className="landing-gradient-text">Finally.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
                Clients, projects, tasks, invoices, campaigns and your calendar — in one calm place,
                under your own brand. Mahano OS writes your morning brief, packs your tasks into the
                hours you actually work, and chases the invoices you'd rather not talk about.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/login?mode=signup" data-testid="hero-primary-cta">
                  <Button size="lg" className="group rounded-full px-6">
                    Start free — no card
                    <ArrowRight className="ml-2 h-4 w-4 landing-arrow" />
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="rounded-full px-6"
                        data-testid="hero-demo-cta" onClick={() => setDemoOpen(true)}>
                  Book a white-label demo
                </Button>
              </div>
              <div className="mt-8 grid max-w-xl grid-cols-1 gap-y-2.5 text-sm text-muted-foreground sm:grid-cols-2">
                {[
                  "Set up in a couple of minutes",
                  "Your logo, colours and wording",
                  "Bring your own email provider",
                  "Export everything as CSV, any time",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--halo-teal))]" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Screenshot stage */}
            <div className="landing-rise landing-delay-2 relative">
              <div className="landing-frame">
                <div className="landing-frame-bar">
                  <span className="landing-dot" style={{ background: "#FF5F57" }} />
                  <span className="landing-dot" style={{ background: "#FEBC2E" }} />
                  <span className="landing-dot" style={{ background: "#28C840" }} />
                  <span className="ml-3 truncate text-[11px] text-white/45">mahano os — {active.label.toLowerCase()}</span>
                </div>
                <img src={active.img} alt={active.label} loading="eager"
                     data-testid={`hero-shot-${active.id}`} className="block w-full" />
              </div>
              {/* Phone frame — same app, same brand */}
              <div className="pointer-events-none absolute bottom-20 -left-12 hidden w-[128px] overflow-hidden rounded-[18px] border-[5px] border-[hsl(var(--mahano-ink))] bg-[hsl(var(--mahano-ink))] shadow-2xl lg:block"
                   data-testid="hero-shot-mobile">
                <img src="/marketing/mobile-today.jpg" alt="Mahano OS on mobile" loading="lazy"
                     className="block w-full" />
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {SHOTS.map((s) => (
                  <button key={s.id} onClick={() => setShot(s.id)} data-testid={`shot-tab-${s.id}`}
                          className={`rounded-full border px-3.5 py-1.5 text-xs ${
                            s.id === shot
                              ? "border-[hsl(var(--studio-primary))] bg-[hsl(var(--studio-primary)/0.10)] text-[hsl(var(--studio-primary))]"
                              : "border-border text-muted-foreground hover:border-[hsl(var(--studio-primary)/0.45)]"
                          }`}
                          style={{ transitionProperty: "color, border-color, background-color", transitionDuration: "160ms" }}>
                    {s.label}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground" data-testid="hero-shot-caption">
                {active.caption}
              </p>
            </div>
          </div>
        </Section>
      </div>

      {/* ---------- Module strip ---------- */}
      <Section className="py-10 md:py-12">
        <div className="rounded-2xl border border-border bg-[hsl(var(--card))] px-6 py-7 md:px-8" data-testid="module-strip">
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-6">
            {[
              ["Calendar", CalendarDays],
              ["Tasks", CheckSquare],
              ["Projects", FolderKanban],
              ["CRM", Users],
              ["Finance", DollarSign],
              ["AI Assistant", Sparkles],
            ].map(([label, Icon]) => (
              <div key={label} data-testid={`module-strip-${label.toLowerCase().replace(/\s+/g, "-")}`}
                   className="flex flex-col items-center gap-2.5 text-center">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-[hsl(var(--studio-primary)/0.10)] text-[hsl(var(--studio-primary))]">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="font-display text-sm">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ---------- Five values ---------- */}
      <Section className="pb-4 pt-2 md:pb-8">
        <div className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-5"
             data-testid="value-band">
          {[
            ["Organise", "Every client, project and dollar in one place."],
            ["Automate", "Briefs, reminders and recurring work run themselves."],
            ["Collaborate", "Your team sees exactly what they should."],
            ["Create", "Campaigns and documents that look like you."],
            ["Live", "Work packed into the hours you actually keep."],
          ].map(([word, line]) => (
            <div key={word} className="bg-[hsl(var(--card))] p-6" data-testid={`value-${word.toLowerCase()}`}>
              <div className="font-display text-lg">{word}</div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{line}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- Replaces strip ---------- */}
      <Section className="py-12">
        <div className="rounded-2xl border border-border bg-[hsl(var(--card))] p-6 md:p-8" data-testid="replaces-strip">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            One workspace instead of
          </p>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {["a CRM", "a project tracker", "a task app", "an invoicing tool", "a scheduling assistant", "a newsletter tool", "three spreadsheets"].map((t) => (
              <span key={t} className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground">
                {t}
              </span>
            ))}
          </div>
        </div>
      </Section>

      {/* ---------- Features bento ---------- */}
      <Section id="features" className="py-16 md:py-24">
        <Eyebrow>What's inside</Eyebrow>
        <h2 className="mt-4 max-w-2xl text-3xl font-semibold tracking-tight sm:text-4xl">
          Twelve modules that already know about each other
        </h2>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
          A task knows its project. A project knows its client. An invoice knows what it billed for.
          Nothing to wire up, no integrations to pay for.
        </p>
        <div className="mt-12 grid gap-4 lg:grid-cols-6">
          {FEATURES.map(({ id, icon: Icon, tag, title, body, span }) => (
            <div key={id} data-testid={`feature-card-${id}`}
                 className={`landing-card group rounded-2xl border border-border bg-[hsl(var(--card))] p-6 md:p-7 ${span}`}>
              <div className="flex items-center gap-2.5">
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-[hsl(var(--studio-primary)/0.10)] text-[hsl(var(--studio-primary))]">
                  <Icon className="h-[18px] w-[18px]" />
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{tag}</span>
              </div>
              <h3 className="mt-5 text-xl font-semibold tracking-tight">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- Scheduling deep dive ---------- */}
      <Section id="scheduling" className="py-16 md:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <Eyebrow>Intelligent calendar</Eyebrow>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
              Stop rebuilding your day by hand
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Mahano treats your time like a resource with real edges. Tell it when you're willing to work —
              and when you're not — then let it place the work.
            </p>
            <div className="mt-8 space-y-5">
              {[
                { icon: Clock, t: "Custom windows", d: "Working hours, after hours, weekends — or invent your own, like “edit mornings only”." },
                { icon: Repeat, t: "Recurring work that behaves", d: "Weekly and monthly tasks regenerate, and you can skip a single occurrence without breaking the series." },
                { icon: CalendarClock, t: "Repacks when life happens", d: "One slip doesn't cascade. Reschedule the day and everything lands somewhere sensible." },
              ].map(({ icon: Icon, t, d }) => (
                <div key={t} className="flex gap-4" data-testid={`scheduling-point-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-border bg-[hsl(var(--card))] text-[hsl(var(--studio-primary))]">
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <div>
                    <div className="text-sm font-medium">{t}</div>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="landing-frame landing-frame-flat">
            <div className="landing-frame-bar">
              <span className="landing-dot" style={{ background: "#FF5F57" }} />
              <span className="landing-dot" style={{ background: "#FEBC2E" }} />
              <span className="landing-dot" style={{ background: "#28C840" }} />
              <span className="ml-3 text-[11px] text-white/45">mahano os — tasks</span>
            </div>
            <img src="/marketing/tasks.jpg" alt="Mahano tasks, scheduled into working windows"
                 loading="lazy" className="block w-full" data-testid="scheduling-shot" />
          </div>
        </div>
      </Section>

      {/* ---------- White-label ---------- */}
      <Section id="whitelabel" className="py-16 md:py-24">
        <div className="overflow-hidden rounded-3xl border border-border bg-[hsl(var(--card))]" data-testid="section-whitelabel">
          <div className="grid gap-10 p-8 md:p-12 lg:grid-cols-2 lg:items-center">
            <div>
              <Eyebrow>White-label</Eyebrow>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
                It doesn't have to look like our product
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Mahano was built to disappear behind your studio. Change the name, the mark, the palette,
                the typeface and the vocabulary — then hand it to your team or your clients as yours.
              </p>
              <Link to="/login?mode=signup" className="mt-8 inline-block" data-testid="whitelabel-cta">
                <Button className="rounded-full px-5">Make it yours <ArrowRight className="ml-2 h-4 w-4 landing-arrow" /></Button>
              </Link>
            </div>
            <ul className="space-y-4">
              {WHITE_LABEL.map(({ icon: Icon, text }) => (
                <li key={text} className="flex gap-3.5 rounded-xl border border-border bg-background p-4">
                  <Icon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-[hsl(var(--halo-teal))]" />
                  <span className="text-sm leading-relaxed text-muted-foreground">{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* ---------- Pricing ---------- */}
      {/* ---------- Proof ---------- */}
      <Section className="py-14 md:py-20">
        <div className="rounded-3xl border border-border bg-[hsl(var(--mahano-ink))] px-6 py-12 md:px-14 md:py-16"
             data-testid="proof-band">
          <Eyebrow>In the field</Eyebrow>
          <div className="mt-6 grid gap-10 lg:grid-cols-3">
            {[
              {
                quote: "We used to run the shoot out of four apps and a group chat. Now the call sheet, the crew and the invoice all sit in the same place — and the morning brief tells me what actually matters.",
                name: "Priya Raman",
                role: "Executive Producer · Northlight Pictures",
              },
              {
                quote: "Our clients see our logo, not someone else's software. Setting up the white-label took an afternoon and we bill it on as part of the retainer.",
                name: "Marcus Oyelaran",
                role: "Founder · Studio Oyelaran",
              },
              {
                quote: "Overdue invoices used to be a Sunday night job. The chasing emails go out on their own now and we've cut the average wait by nineteen days.",
                name: "Hannah Beck",
                role: "Head of Production · Bellbird Collective",
              },
            ].map((t) => (
              <figure key={t.name} className="flex flex-col" data-testid={`proof-${t.name.split(" ")[0].toLowerCase()}`}>
                <Quote className="h-5 w-5 text-[hsl(var(--mahano-jade))]" strokeWidth={2} />
                <blockquote className="mt-4 text-base leading-relaxed text-[hsl(var(--mahano-sand))]">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-5 text-sm">
                  <span className="font-medium text-[hsl(var(--mahano-ivory))]">{t.name}</span>
                  <span className="block text-xs text-[hsl(var(--mahano-sand))]/70">{t.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </Section>

      <Section id="pricing" className="py-16 md:py-24">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div className="lg:pt-2">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
              One price. Every module.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              No per-seat maths, no module unlocked at the “growth” tier, no sales call to find out the number.
              Start free, and only pay when Mahano is actually running your week.
            </p>
          </div>
          <div className="landing-price rounded-3xl border border-border bg-[hsl(var(--card))] p-8 md:p-10" data-testid="pricing-card">
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-semibold tracking-tight" data-testid="pricing-amount">$49</span>
              <span className="text-sm text-muted-foreground">/ month, flat</span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">Free while you set it up. Cancel whenever.</p>
            <Link to="/login?mode=signup" className="mt-7 block" data-testid="pricing-cta">
              <Button size="lg" className="w-full rounded-full">Start free — no card</Button>
            </Link>
            <div className="mt-8 space-y-3 border-t border-border pt-7">
              {PRICE_INCLUDES.map((t) => (
                <div key={t} className="flex gap-3">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--halo-teal))]" />
                  <span className="text-sm leading-relaxed text-muted-foreground">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ---------- FAQ ---------- */}
      <Section id="faq" className="py-16 md:py-24">
        <Eyebrow>Questions</Eyebrow>
        <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">The things people ask first</h2>
        <div className="mt-10 divide-y divide-border rounded-2xl border border-border bg-[hsl(var(--card))]">
          {FAQ.map((item, i) => (
            <div key={item.q}>
              <button className="flex w-full items-center gap-4 p-6 text-left"
                      data-testid={`faq-trigger-${i}`}
                      onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                <span className="text-base font-medium">{item.q}</span>
                <span className={`ml-auto text-muted-foreground ${openFaq === i ? "landing-rotated" : ""}`}
                      style={{ transitionProperty: "transform", transitionDuration: "200ms" }}>+</span>
              </button>
              {openFaq === i && (
                <p className="px-6 pb-6 text-sm leading-relaxed text-muted-foreground" data-testid={`faq-answer-${i}`}>
                  {item.a}
                </p>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- Final CTA ---------- */}
      <Section className="pb-24">
        <div className="relative overflow-hidden rounded-3xl border border-border p-10 md:p-16" data-testid="final-cta">
          <div className="landing-cta-glow" aria-hidden="true" />
          <div className="relative max-w-2xl">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              Your studio deserves better than eleven browser tabs
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Sign up, brand it, import your clients, and let Mahano take the first pass at tomorrow.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login?mode=signup" data-testid="final-cta-button">
                <Button size="lg" className="rounded-full px-6">
                  Start free — no card <ArrowRight className="ml-2 h-4 w-4 landing-arrow" />
                </Button>
              </Link>
              <Link to="/login" data-testid="final-signin-button">
                <Button size="lg" variant="outline" className="rounded-full px-6">Sign in</Button>
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* ---------- Footer ---------- */}
      <footer className="border-t border-border px-6 py-10 md:px-10 lg:px-16" data-testid="landing-footer">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-2.5">
            <HaloRing size={22} />
            <HaloWordmark height={14} />
          </div>
          <p className="text-xs text-muted-foreground md:ml-6">
            The white-label operating system for creative studios.
          </p>
          <div className="flex gap-5 text-xs text-muted-foreground md:ml-auto">
            <a href="#features" data-testid="footer-link-features">Features</a>
            <a href="#pricing" data-testid="footer-link-pricing">Pricing</a>
            <Link to="/login" data-testid="footer-link-signin">Sign in</Link>
          </div>
        </div>
      </footer>

      <DemoRequestDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
